import {
  PaymentIntentConfirmParams,
  PaymentMethodCreateParams,
} from '@stripe/stripe-js';

import {
  EBillingAddress,
  ICheckoutFormValues,
} from '@jpp/organisms/CheckoutForm/types';
import { getShippingCountryCode } from '@jpp/organisms/CheckoutForm/utils';
import { IAuthStoreState } from 'common/redux/auth/reducer';
import {
  ICartCoupon,
  ICartLineItem,
} from 'common/redux/wooCommerce/cart/reducer';
import { IOrderCouponLine } from 'common/redux/wooCommerce/orders/reducer';
import {
  IWooCommerceAPIBilling,
  IWooCommerceAPIShipping,
} from 'common/utils/transformers/order.transformer';

export interface IOrderLineItem {
  product_id: number;
  quantity: number;
  variation_id?: number;
}

export interface ISubmitOrderShippingLine {
  method_id: string;
  method_title: string;
  total: string;
  taxes: Record<string, number>;
}

export interface ISubmitOrderBilling {
  first_name: string;
  last_name: string;
  company?: string;
  address_1: string;
  address_2?: string;
  city: string;
  state?: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
}

export interface ISubmitOrderShipping {
  first_name: string;
  last_name: string;
  address_1: string;
  address_2?: string;
  city: string;
  state?: string;
  postcode: string;
  country: string;
}

export interface ISubmitOrder {
  payment_method: string;
  payment_method_title: string;
  set_paid: boolean;
  customer_id: number;
  customer_note?: string;
  transaction_id?: string;
  line_items: IOrderLineItem[];
  billing: ISubmitOrderBilling;
  shipping?: ISubmitOrderShipping;
  shipping_lines?: Omit<ISubmitOrderShippingLine, 'taxes'>[];
  coupon_lines?: IOrderCouponLine[];
}

export const orderLineItem = (items: ICartLineItem[]): IOrderLineItem[] =>
  items.map(({ product_id, quantity }) => ({
    product_id,
    quantity,
  }));

export const getCheckoutBillingAndShipping = ({
  billing_company,
  billing_address_1,
  billing_address_2,
  billing_city,
  billing_country,
  billing_first_name,
  billing_last_name,
  billing_postcode,
  billing_state,
  shipping_address_1,
  shipping_address_2,
  shipping_city,
  shipping_company,
  shipping_country,
  shipping_first_name,
  shipping_last_name,
  shipping_postcode,
  shipping_state,
}: ICheckoutFormValues) => {
  const billing = {
    first_name: billing_first_name,
    last_name: billing_last_name,
    company: billing_company,
    address_1: billing_address_1,
    address_2: billing_address_2,
    city: billing_city,
    state: billing_state,
    country: billing_country,
    postcode: billing_postcode,
  };

  const shipping = {
    first_name: shipping_first_name,
    last_name: shipping_last_name,
    company: shipping_company,
    address_1: shipping_address_1,
    address_2: shipping_address_2,
    city: shipping_city,
    state: shipping_state,
    country: shipping_country,
    postcode: shipping_postcode,
  };

  return {
    billing,
    shipping,
  };
};

export const mapCheckoutFieldsToOrder = (
  checkout: ICheckoutFormValues,
  { taxes, ...shippingMethod }: ISubmitOrderShippingLine,
  authUser: IAuthStoreState,
  coupons: ICartCoupon[],
  hasOnlyVirtualProducts: boolean
): ISubmitOrder => {
  const {
    billing_country,
    shipping_country,
    email,
    phone,
    alternate_billing_address,
    ...rest
  } = checkout;

  const isAlternateBilling =
    alternate_billing_address === EBillingAddress.Billing;
  const { shipping, billing } = getCheckoutBillingAndShipping(checkout);

  return {
    ...rest,
    set_paid: false,
    customer_id: authUser.id || 0,
    billing: {
      ...(isAlternateBilling ? billing : shipping),
      country: hasOnlyVirtualProducts ? billing_country : shipping_country,
      email,
      phone,
    },
    shipping: hasOnlyVirtualProducts ? undefined : shipping,
    shipping_lines: hasOnlyVirtualProducts ? undefined : [shippingMethod],
    coupon_lines: coupons.map((coupon) => ({ code: coupon?.coupon })),
  };
};

export const getStripeBilling = (
  address?: IWooCommerceAPIShipping | IWooCommerceAPIBilling
): PaymentMethodCreateParams.BillingDetails | undefined => {
  if (!address) {
    return;
  }
  const { first_name, last_name, address_1, address_2, city, postcode, state } =
    address;
  const country = address.country
    ? getShippingCountryCode(address.country)
    : undefined;

  const email =
    'email' in address && Boolean(address.email)
      ? { email: address.email }
      : {};
  const phone =
    'phone' in address && Boolean(address.phone)
      ? { phone: address.phone }
      : {};

  return {
    name: `${first_name} ${last_name}`,
    address: {
      city,
      country,
      line1: address_1,
      line2: address_2,
      postal_code: postcode,
      state,
    },
    ...email,
    ...phone,
  };
};

export const getStripeShipping = (
  shipping?: IWooCommerceAPIShipping
): PaymentIntentConfirmParams.Shipping | undefined => {
  if (!shipping) {
    return;
  }
  const { address, name } = (getStripeBilling(shipping) ||
    {}) as PaymentIntentConfirmParams.Shipping;
  return {
    name: name!,
    address,
  };
};
